import { forwardRef, type ReactNode } from 'react';
import { MediaUpload, type MediaUploadValue } from './MediaUpload';
import { filesUploader } from './helpers';

export interface ImageFieldProps {
  /** Bound value is a plain URL string (form payload is unchanged). */
  value?: string | null;
  onChange?: (url: string | null) => void;
  /** Label shown under the preview thumbnail. */
  label?: string;
  accept?: string;
  maxSizeMB?: number;
  hint?: ReactNode;
  disabled?: boolean;
}

/**
 * Form-friendly single image field. Keeps the bound value a URL string so it
 * drops into existing AntD `FormField`/react-hook-form fields WITHOUT changing
 * the submit payload, but the user uploads via the real files API and gets a
 * preview + delete from <MediaUpload/>. Replaces "paste an image URL" inputs.
 *
 * `FormField` injects `value`/`onChange`/`ref`/`name` via cloneElement: only
 * `value`/`onChange` are consumed, the ref is forwarded to a wrapper (so RHF
 * focus-on-error works without a function-component ref warning), and no extra
 * props leak onto <MediaUpload/>.
 */
export const ImageField = forwardRef<HTMLDivElement, ImageFieldProps>(function ImageField(
  { value, onChange, label, accept = 'image/*', maxSizeMB, hint, disabled },
  ref,
) {
  const mediaValue: MediaUploadValue | null = value ? { url: value, name: label } : null;
  return (
    <div ref={ref}>
      <MediaUpload
        value={mediaValue}
        onChange={(next) => onChange?.(next?.url ?? null)}
        uploader={filesUploader}
        accept={accept}
        maxSizeMB={maxSizeMB}
        hint={hint}
        disabled={disabled}
      />
    </div>
  );
});
